import { useState, useMemo } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight, CheckCircle2, FileText, ClipboardList, Download, X, Lock, Quote, TrendingUp } from "lucide-react";
import { SERVICES } from "@/data/services";
import { SERVICE_ARTICLES, getRequirements } from "@/data/articles";
import { getCaseStudies } from "@/data/caseStudies";
import useSeo from "@/hooks/useSeo";
import QuoteBlock from "@/components/QuoteBlock";
import { withAttribution } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNLOCK_KEY = "triaxis_checklist_unlocked_v1";

export default function ServiceDetail() {
  const { slug, subSlug } = useParams();
  const navigate = useNavigate();
  const service = SERVICES.find((s) => s.slug === slug);
  const active = service && subSlug ? service.subpages.find((p) => p.slug === subSlug) : null;
  const [gateOpen, setGateOpen] = useState(false);
  useSeo({
    title: service ? `${service.name}${active ? " · " + active.name : ""} — Tri-Axis XYZ` : "Service — Tri-Axis XYZ",
    description: service ? service.short : "CAD drafting service",
    image: service ? `${API}/og/${service.slug}` : undefined,
  });

  // Memoize the static "Related Services" computation so it doesn't re-run on every render.
  // Hook must run before any conditional return — guard against missing service inside the callback.
  const relatedServices = useMemo(
    () => (service ? SERVICES.filter((s) => s.slug !== service.slug).slice(0, 5) : []),
    [service]
  );

  if (!service) return <Navigate to="/services" replace />;

  const isUnlocked = () => {
    try { return !!JSON.parse(sessionStorage.getItem(UNLOCK_KEY) || "null"); } catch { return false; }
  };

  const triggerDownload = () => {
    window.open(`${API}/checklist/${service.slug}`, "_blank", "noopener,noreferrer");
  };

  const onChecklistClick = (e) => {
    e.preventDefault();
    if (isUnlocked()) { triggerDownload(); return; }
    setGateOpen(true);
  };

  return (
    <div data-testid="page-service-detail">
      {/* Breadcrumb */}
      <div className="border-b border-slate-200 bg-paper-dark/40">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-4 mono text-[11px] tracking-[0.16em] uppercase text-slate-500 flex flex-wrap items-center gap-2">
          <Link to="/" className="hover:text-slate-900">Home</Link>
          <span>/</span>
          <Link to="/services" className="hover:text-slate-900">Services</Link>
          <span>/</span>
          <Link to={`/services/${service.slug}`} className="hover:text-slate-900 text-topo">{service.name}</Link>
          {active && (<><span>/</span><span className="text-emerald-700">{active.name}</span></>)}
        </div>
      </div>

      {/* Hero */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <span className="text-4xl">{service.emoji}</span>
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">CAD Service · {String(SERVICES.findIndex(s=>s.slug===slug)+1).padStart(2,"0")}</div>
            </div>
            <h1 className="display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900">
              {service.name}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-3xl leading-relaxed">
              {active ? active.body : service.hero}
            </p>
          </div>

          <aside className="lg:col-span-4">
            <div className="border border-slate-200 p-6 bg-paper-dark/40/70">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700">Deliverables</div>
              <ul className="mt-4 space-y-2">
                {service.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-700 shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* Subpages */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Sub-services</div>
          <h2 className="display mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-slate-900">
            What's included under {service.name}.
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {service.subpages.map((p) => {
              const isActive = active?.slug === p.slug;
              return (
                <Link
                  key={p.slug}
                  to={`/services/${service.slug}/${p.slug}`}
                  data-testid={`subpage-${p.slug}`}
                  className={`tri-card bg-white p-6 lg:p-7 group block ${isActive ? "ring-2 ring-orange-survey" : ""}`}
                >
                  <div className="display text-lg font-extrabold tracking-tight text-slate-900">{p.name}</div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.body}</p>
                  <div className="mt-5 flex items-center gap-1 text-sm font-semibold text-topo">
                    Read full article <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Full article */}
      <section className="border-b border-slate-200 bg-paper-dark/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">In-depth · About this service</div>
            <h2 className="display mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-slate-900">
              What <span className="text-emerald-700">{service.name}</span> covers in our drafting workflow.
            </h2>
            <div className="mt-5 inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
              <FileText className="h-3.5 w-3.5" /> ~3 min read
            </div>
          </div>
          <article className="lg:col-span-8 space-y-5 text-slate-700 leading-relaxed">
            {(SERVICE_ARTICLES[service.slug] || [service.hero]).map((p, i) => (
              <p key={`${service.slug}-art-${i}`} data-testid={`service-article-p-${i}`} className="text-base sm:text-lg">{p}</p>
            ))}
            {active && (
              <div className="mt-6 border-l-2 border-emerald-700 pl-5 bg-white p-5 rounded-sm">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700">
                  Sub-service focus · {active.name}
                </div>
                <p className="mt-2 text-base text-slate-700">{active.body}</p>
              </div>
            )}
          </article>
        </div>
      </section>

      {/* Requirements — what we need to get started */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Getting started · Inputs we need</div>
              <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
                What you send us → <span className="text-emerald-700">what we deliver</span>.
              </h2>
            </div>
            <div className="lg:col-span-5 text-slate-600">
              Send the items below and we'll produce an accurate, sealed CAD deliverable to your standards.
              <button
                onClick={onChecklistClick}
                type="button"
                data-testid="checklist-download-btn"
                className="mt-4 inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-4 py-2.5 transition-colors"
              >
                <Download className="h-4 w-4" /> Download CAD Submission Checklist (PDF)
              </button>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200" data-testid="requirements-list">
            {getRequirements(service.slug).map((r, i) => (
              <li key={r.t} className="bg-white p-6 tri-card" data-testid={`req-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center bg-orange-survey text-white rounded-sm shrink-0">
                    <ClipboardList className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="display text-base font-extrabold tracking-tight text-slate-900">{r.t}</div>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{r.d}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-6 mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
            Missing an item? Send what you have — our senior CAD lead will flag any gaps before production starts.
          </p>
        </div>
      </section>

      {/* Case studies */}
      <CaseStudiesBlock serviceSlug={service.slug} serviceName={service.name} studies={getCaseStudies(service.slug)} />

      {/* Quote form */}
      <QuoteBlock serviceSlug={service.slug} serviceName={service.name} onSuccess={() => navigate("/contact")} />
      <section className="border-b border-slate-200 bg-paper-dark/40">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Related Services</div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200">
            {relatedServices.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`related-${s.slug}`}
                className="tri-card bg-white p-5"
              >
                <div className="text-2xl">{s.emoji}</div>
                <div className="mt-3 display text-sm font-extrabold tracking-tight text-slate-900">{s.name}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      {gateOpen && (
        <ChecklistGate
          service={service}
          onClose={() => setGateOpen(false)}
          onUnlocked={() => {
            try {
              sessionStorage.setItem(UNLOCK_KEY, JSON.stringify({ at: Date.now() }));
            } catch (err) {
              // sessionStorage may be unavailable (private mode, quota). Non-fatal: the user can still download.
              if (process.env.NODE_ENV !== "production") {
                console.warn("Could not persist checklist unlock flag:", err);
              }
            }
            setGateOpen(false);
            triggerDownload();
          }}
        />
      )}
    </div>
  );
}

function ChecklistGate({ service, onClose, onUnlocked }) {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and work email are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, withAttribution({
        name: form.name,
        email: form.email,
        company: form.company || undefined,
        service: service.name,
        message: `Requested ${service.name} CAD submission checklist PDF.`,
        source: `checklist-${service.slug}`,
      }));
      toast.success("Unlocked. Your checklist is downloading.");
      onUnlocked();
    } catch (err) {
      toast.error("Could not unlock. Please email triaxisxyzsolutions@gmail.com");
    } finally { setLoading(false); }
  };

  return (
    <div
      data-testid="checklist-gate"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-cyanotype/70 backdrop-blur-sm" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md border border-slate-200 bg-white p-8 shadow-2xl rounded-sm"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="checklist-gate-close"
          aria-label="Close"
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-900"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700 flex items-center gap-2">
          <Lock className="h-3 w-3" /> One quick detail
        </div>
        <h2 className="display mt-2 text-2xl font-extrabold tracking-tighter text-slate-900">
          Unlock the {service.name} checklist
        </h2>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          Tell us where to send updates and we'll start your PDF download. No spam — used only to follow up if you have project questions.
        </p>
        <div className="mt-5 space-y-3">
          <GateField label="Full name" id="cl-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="checklist-gate-name" />
          <GateField label="Work email" id="cl-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="checklist-gate-email" />
          <GateField label="Company (optional)" id="cl-company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="checklist-gate-company" />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="checklist-gate-submit"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark disabled:opacity-60 text-white text-sm font-semibold px-5 py-3"
        >
          <Download className="h-4 w-4" /> {loading ? "Unlocking..." : "Unlock & download PDF"}
        </button>
        <p className="mt-3 mono text-[10px] tracking-[0.18em] uppercase text-slate-400 text-center">
          1-page, branded, ~50 KB
        </p>
      </form>
    </div>
  );
}

function GateField({ label, id, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label htmlFor={id} className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full border border-slate-300 focus:border-emerald-700 outline-none p-2.5 text-sm rounded-sm bg-white"
      />
    </div>
  );
}

function CaseStudiesBlock({ serviceSlug, serviceName, studies }) {
  if (!studies || studies.length === 0) return null;
  return (
    <section className="border-b border-slate-200 bg-white" data-testid="case-studies-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700 flex items-center gap-2">
              <Quote className="h-3 w-3" /> Recent work · case studies
            </div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
              {serviceName} in production.
            </h2>
          </div>
          <p className="lg:col-span-5 text-slate-600">
            A snapshot of recent {serviceName.toLowerCase()} projects we've drafted for survey firms across North America and Australia. Names redacted on request.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {studies.map((c, i) => (
            <Link
              to={`/services/${serviceSlug}/case-studies/${c.slug}`}
              key={c.slug}
              data-testid={`case-study-${i}`}
              className="bg-white p-6 lg:p-8 flex flex-col group hover:bg-paper-dark/40 transition-colors"
            >
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
                Case study · {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="display mt-2 text-xl lg:text-2xl font-extrabold tracking-tight text-slate-900">
                {c.title}
              </h3>
              <div className="mt-1 mono text-[11px] tracking-[0.14em] uppercase text-slate-500">
                Client · {c.client}
              </div>
              <dl className="mt-5 space-y-4 text-sm leading-relaxed">
                <div>
                  <dt className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">Challenge</dt>
                  <dd className="mt-1 text-slate-700">{c.challenge}</dd>
                </div>
                <div>
                  <dt className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700">Outcome</dt>
                  <dd className="mt-1 text-slate-700">{c.outcome}</dd>
                </div>
              </dl>
              {c.metrics && c.metrics.length > 0 && (
                <ul className="mt-6 flex flex-wrap gap-2">
                  {c.metrics.map((m) => (
                    <li
                      key={m}
                      className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.14em] uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-sm"
                    >
                      <TrendingUp className="h-3 w-3" /> {m}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-6 flex items-center gap-1 text-sm font-semibold text-topo">
                Read full case study <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
